import React from 'react';
import {UserInsights} from "types/User";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import StatList from "components/StatList";
import StatBoxSimple from "components/StatBoxSimple";
import Divider from "components/Divider";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import Link from "components/Link";
import DotBadge from "components/DotBadge";
import {formattedDate} from "services/date";
import Card from "components/Card";
import MembershipStatus from "types/MembershipStatus";

// Types ----

type MembershipsCardProps = {
  userId: number
  insights?: UserInsights | null
}

// Helpers ----

const statusColors: Record<MembershipStatus, number> = {
  [MembershipStatus.UNPAID]: DotBadge.colors.YELLOW,
  [MembershipStatus.PAID]: DotBadge.colors.BLUE,
  [MembershipStatus.VALID]: DotBadge.colors.GREEN,
  [MembershipStatus.EXPIRED]: DotBadge.colors.GRAY,
  [MembershipStatus.REVOKED]: DotBadge.colors.RED,
}

// Component ----

function MembershipsCard({userId, insights}: MembershipsCardProps) {

  const {data: memberships, isLoading: isLoadingMemberships} = useQuery(
    ['memberships.all', {owner: userId}],
    () => ngage.memberships.filtered({owner: userId}),
    {keepPreviousData: true}
  )

  return (
    <Card heading="Memberships" isLoading={isLoadingMemberships}>
      <StatList columns={2}>
        <StatBoxSimple name='Total' stat={insights?.engagement.all_memberships}/>
        <StatBoxSimple name='Valid' stat={insights?.engagement.valid_memberships}/>
        <StatBoxSimple name='Spent' stat={insights?.engagement.membership_spend_formatted}/>
      </StatList>

      {!memberships?.data?.length ? (
        <div className="mt-6 flow-root">
          <Divider/>
          <p className="text-sm text-gray-400 mt-6">No memberships</p>
        </div>
      ) : (
        <div className="mt-6">
          <StackedList>
            {memberships?.data?.map((membership) => (
              <StackedListItem hasLink={true} key={membership.id}>
                <Link to={`/memberships/${membership.id}`}>
                  <div className="flex items-center">
                    <p className="truncate text-sm font-medium text-gray-500">{membership.plan.name}</p>

                    <div className="ml-3 flex flex-shrink-0">
                      <DotBadge color={statusColors[membership.status]} text={membership.status}/>
                    </div>

                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-1 font-normal text-sm text-gray-900">
                    <p>
                      <span className="mr-1.5 text-gray-400">Number:</span>
                      {membership.number}
                    </p>
                    <p>
                      <span className="mr-1.5 text-gray-400">Expires:</span>
                      {formattedDate(membership.expires_at)}
                    </p>
                  </div>
                </Link>
              </StackedListItem>
            ))}
          </StackedList>
        </div>
      )}
    </Card>
  );
}

// Export ----

export default MembershipsCard;